import React, { useEffect, useState } from 'react';
import { CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  subscribePendingPersonaApprovals,
  reviewPersonaApproval,
} from '../../services/personaApprovalService';

function formatAddress(row) {
  const parts = [
    row.personalAddressStreet,
    row.personalAddressCity,
    row.personalAddressProvince,
    row.personalAddressPostalCode,
    row.personalAddressCountry,
  ].filter(Boolean);
  return parts.join(', ') || '—';
}

export default function AccountApprovalsAdminPage() {
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    return subscribePendingPersonaApprovals(setRows);
  }, []);

  const handleReview = async (uid, approve) => {
    let rejectionReason = '';
    if (!approve) {
      rejectionReason = window.prompt(t('admin.approvals.rejectReasonPrompt')) || '';
    }
    setBusyId(uid);
    setMessage(null);
    try {
      await reviewPersonaApproval({
        targetUid: uid,
        approve,
        rejectionReason: rejectionReason.trim() || undefined,
      });
      setMessage(t(approve ? 'admin.approvals.approved' : 'admin.approvals.rejected'));
    } catch (err) {
      console.error(err);
      setMessage(err?.message || 'Error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.approvals.title')}</h1>
          <Link to="/" className="text-sm text-blue-600 hover:underline">
            {t('auth.goToHome')}
          </Link>
        </div>
        {message ? (
          <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            {message}
          </p>
        ) : null}
        {rows.length === 0 ? (
          <p className="text-gray-600 bg-white rounded-lg border p-6">{t('admin.approvals.empty')}</p>
        ) : (
          <ul className="space-y-4">
            {rows.map((row) => (
              <li key={row.uid || row.id} className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                  <p>
                    <span className="text-gray-500">{t('admin.approvals.email')}:</span>{' '}
                    <span className="font-medium">{row.email || '—'}</span>
                  </p>
                  <p>
                    <span className="text-gray-500">{t('admin.approvals.persona')}:</span>{' '}
                    <span className="font-medium">
                      {row.persona ? t(`persona.${row.persona}`) : '—'}
                    </span>
                  </p>
                  <p>
                    <span className="text-gray-500">{t('admin.approvals.company')}:</span>{' '}
                    <span className="font-medium">{row.companyName || '—'}</span>
                  </p>
                  <p>
                    <span className="text-gray-500">{t('admin.approvals.phone')}:</span>{' '}
                    <span className="font-medium">{row.phone || '—'}</span>
                  </p>
                  <p className="sm:col-span-2">
                    <span className="text-gray-500">{t('admin.approvals.address')}:</span>{' '}
                    <span className="font-medium">{formatAddress(row)}</span>
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <button
                    type="button"
                    disabled={busyId === row.uid}
                    onClick={() => handleReview(row.uid, true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {t('admin.approvals.approve')}
                  </button>
                  <button
                    type="button"
                    disabled={busyId === row.uid}
                    onClick={() => handleReview(row.uid, false)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-200 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    <XCircle className="w-4 h-4" />
                    {t('admin.approvals.reject')}
                  </button>
                  {busyId === row.uid ? (
                    <RefreshCw className="w-4 h-4 animate-spin text-gray-400 self-center" />
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
